import {eachMonthOfInterval, getDate, getMonth, getYear, startOfYear} from 'date-fns';
import {Choice} from 'platform/components';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {useGetPayPerUseLeaderboardQuery, useGetPayPerUseMetricsQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';

import {getApiDateString, parseDate} from 'shared/web';

import {PayPerUseKpiHeader} from './components/PayPerUseKpiHeader';
import {PayPerUseLeaderboardTable} from './components/PayPerUseLeaderboardTable';
import {PayPerUseOperationCards} from './components/PayPerUseOperationCards';
import {PAY_PER_USE_MONTH_NAME_KEYS} from './constants/PAY_PER_USE_MONTH_NAME_KEYS';
import {getEnglishOrdinal} from './utils/getEnglishOrdinal';
import {getOperationTypeColumns} from './utils/getOperationTypeColumns';

const MONTH_VALUE_LENGTH = 7;
const UTC_DATE_LENGTH = 10;
const ENGLISH_LANGUAGE = 'en';
const FIRST_DAY_OF_MONTH = 1;

export function PayPerUseMetrics() {
  // Derive the month from the UTC date per the backend contract.
  const today = parseDate(new Date().toISOString().slice(0, UTC_DATE_LENGTH));
  const isEnglish = i18n.language?.split('-')[0] === ENGLISH_LANGUAGE;
  const from = isEnglish ? getEnglishOrdinal(FIRST_DAY_OF_MONTH) : FIRST_DAY_OF_MONTH.toString();
  const to = isEnglish ? getEnglishOrdinal(getDate(today)) : getDate(today).toString();
  const monthOptions = eachMonthOfInterval({start: startOfYear(today), end: today})
    .reverse()
    .map((date) => {
      const value = getApiDateString(date).slice(0, MONTH_VALUE_LENGTH);
      const monthLabel = `${i18n.t(PAY_PER_USE_MONTH_NAME_KEYS[getMonth(date)])} ${getYear(date)}`;
      const label =
        getMonth(date) === getMonth(today) ? `${monthLabel} (${from} - ${to})` : monthLabel;
      return {value, label};
    });
  const [month, setMonth] = useState(monthOptions[0].value);

  const {
    data: metricsData,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
  } = useGetPayPerUseMetricsQuery({month});
  const {
    data: leaderboardData,
    isLoading: isLeaderboardLoading,
    isError: isLeaderboardError,
  } = useGetPayPerUseLeaderboardQuery({month});

  return (
    <SettingsTemplate
      data-testid={testIds.settings.payPerUseMetrics('template')}
      header={{
        title: i18n.t('page.homepage.payPerUse.title'),
      }}
      isLoading={isMetricsLoading || isLeaderboardLoading}
      isError={isMetricsError || isLeaderboardError}
    >
      <Show
        when={
          isNotNil(metricsData) && isNotNil(leaderboardData) ? {metricsData, leaderboardData} : null
        }
      >
        {(payPerUse) => (
          <VStack spacing={6}>
            <HStack justify="space-between" align="center" spacing={4}>
              <Text size="small" color="secondary">
                {i18n.t('page.payPerUseMetrics.description')}
              </Text>
              <HStack align="center" spacing={3}>
                <Choice<string>
                  value={month}
                  options={monthOptions}
                  onChange={(value) => {
                    if (isNotNil(value)) {
                      setMonth(value);
                    }
                  }}
                  aria-label={i18n.t('bigCalendar.month')}
                  menuInPortal
                  isNotClearable
                  data-testid={testIds.settings.payPerUseMetrics('month')}
                />
              </HStack>
            </HStack>

            <PayPerUseKpiHeader
              metrics={payPerUse.metricsData}
              data-testid={testIds.settings.payPerUseMetrics('kpi')}
            />

            <VStack spacing={3}>
              <Heading size={4}>{i18n.t('page.payPerUseMetrics.operationCards.title')}</Heading>
              <PayPerUseOperationCards
                operationTypes={payPerUse.metricsData.operationTypes}
                totalCount={payPerUse.metricsData.totalCount}
                data-testid={testIds.settings.payPerUseMetrics('operationCards')}
              />
            </VStack>

            <VStack spacing={3}>
              <Heading size={4}>{i18n.t('page.payPerUseMetrics.leaderboard.title')}</Heading>
              <PayPerUseLeaderboardTable
                // Reset table state when the month changes.
                key={payPerUse.metricsData.month}
                month={payPerUse.metricsData.month}
                users={payPerUse.leaderboardData.users}
                operationColumns={getOperationTypeColumns(payPerUse.metricsData.operationTypes)}
                data-testid={testIds.settings.payPerUseMetrics('leaderboard')}
              />
            </VStack>
          </VStack>
        )}
      </Show>
    </SettingsTemplate>
  );
}
